// Records namespace (window.backlit.records) — a public-read, owner-write
// store between data.* (shared read+write) and userdata.* (private per-user).
// Backed by the data API at /_backlit/records{,/{key}}.
//
// Access model (CLAUDE.md §8/§9): any signed-in user creates records and reads
// everyone's; only the record's creator (by uid, email-hash fallback) or an
// admin may overwrite/delete it. get/list carry the owner uid; reassign is
// admin-only. Records requires a signed-in user — like userdata.*, the server
// returns 401 unauthenticated for an anonymous / signed-out visitor, so we
// apply the same message rewrite. The SDK does NOT pre-check ownership or
// admin; the server is the boundary (a non-owner write throws `forbidden`).

import { normalizeBody } from "./body.js";
import { BacklitError } from "./errors.js";
import {
  getBackend,
  type ListOptions,
  type ListPage,
  type GetBatchOptions,
  type RecordBatchEntry,
  type PutResult,
  type RecordResult,
  type RecordListEntry,
  type RecordPutIfMatchResult,
} from "./backend.js";
import { validateDataKey, validateDataPrefix, validateOwnerUid } from "./validate.js";
import {
  JSON_CONTENT_TYPE,
  encodeJSON,
  decodeJSON,
  decodeBatchJSONPage,
  carryTags,
  runUpdateJSONWithOwner,
  type RecordJSONResult,
  type RecordBatchJSONEntry,
  type RecordPutJSONIfMatchResult,
  type UpdateJSONOptions,
} from "./json.js";

export interface RecordsNamespace {
  // get reads any record (records are public-read). Resolves the value plus
  // the owner uid of the record's creator. Throws not_found on a miss.
  get(key: string): Promise<RecordResult>;
  // put creates a record (first write claims it) or overwrites your own. A
  // non-creator/non-admin overwriting a foreign record throws `forbidden`.
  // Viewers CAN create (records are owner-scoped, not the data.* viewer block).
  put(key: string, body: BodyInit, contentType: string): Promise<PutResult>;
  // putIfMatch is the records compare-and-swap. Same 200-match / mismatch-
  // witness contract as data.putIfMatch, plus: the witness carries the owner
  // (so updateJSON sees who owns a key on a mismatch), and a matching write by
  // a non-creator/non-admin throws `forbidden`. Empty expectedCrc32c =
  // create-only (claim).
  putIfMatch(key: string, body: BodyInit, contentType: string, expectedCrc32c: string): Promise<RecordPutIfMatchResult>;
  // delete removes a record you created (or any record, as an admin). Throws
  // `forbidden` for a non-creator/non-admin, not_found on a miss.
  delete(key: string): Promise<void>;
  // list returns one bounded page of record keys (each with its owner uid)
  // plus an opaque `next` cursor (DATA-1); same pagination contract as
  // data.list. Paginate until next is "".
  list(prefix?: string, opts?: ListOptions): Promise<ListPage<RecordListEntry>>;

  // getBatch is the value-bearing sibling of list (identical contract to
  // data.getBatch), each entry additionally carrying the record's owner uid.
  getBatch(prefix?: string, opts?: GetBatchOptions): Promise<ListPage<RecordBatchEntry>>;

  // JSON convenience — identical contract to data.getJSON / putJSON /
  // putJSONIfMatch, carrying the owner uid (get/putIfMatch/update).
  getJSON<T = unknown>(key: string): Promise<RecordJSONResult<T>>;
  // getBatchJSON — getBatch with each included value JSON.parsed (identical
  // contract to data.getBatchJSON); each entry carries the owner uid.
  getBatchJSON<T = unknown>(prefix?: string, opts?: GetBatchOptions): Promise<ListPage<RecordBatchJSONEntry<T>>>;
  putJSON(key: string, value: unknown): Promise<PutResult>;
  putJSONIfMatch<T = unknown>(key: string, value: unknown, expectedCrc32c: string): Promise<RecordPutJSONIfMatchResult<T>>;
  // updateJSON — read-modify-write CAS over a record. Same contract as
  // data.updateJSON; the committed result carries the owner. A `forbidden`
  // (non-creator/non-admin) propagates immediately, it does not loop.
  updateJSON<T = unknown>(key: string, mutate: (current: T | undefined) => T, options?: UpdateJSONOptions): Promise<RecordJSONResult<T>>;

  // reassign moves a record's owner to newOwnerUid. ADMIN-ONLY — the SDK does
  // NOT pre-check admin; the server returns `forbidden` for a non-admin. The
  // newOwnerUid is opaque/server-issued (a uid or email hash); the SDK only
  // checks it is a non-empty string. Resolves void on success (server 204).
  reassign(key: string, newOwnerUid: string): Promise<void>;
}

const RECORDS_UNAUTH_MESSAGE =
  "window.backlit.records.* requires a signed-in user. " +
  "Call window.backlit.auth.me() to check first; an anonymous or signed-out " +
  "visitor cannot use this surface.";

function rewriteUnauth(e: unknown): never {
  if (e instanceof BacklitError && e.code === "unauthenticated") {
    throw new BacklitError("unauthenticated", RECORDS_UNAUTH_MESSAGE, e.status);
  }
  throw e;
}

export function createRecords(): RecordsNamespace {
  // const ns so the JSON methods compose the rewrite-wrapped base methods
  // (ns.get / ns.put / ns.putIfMatch) and inherit the unauthenticated rewrite.
  const ns: RecordsNamespace = {
    async get(key: string): Promise<RecordResult> {
      validateDataKey(key);
      try {
        return await getBackend().recordsGet(key);
      } catch (e) {
        rewriteUnauth(e);
      }
    },

    async put(key: string, body: BodyInit, contentType: string): Promise<PutResult> {
      validateDataKey(key);
      const norm = normalizeBody(body, contentType);
      try {
        return await getBackend().recordsPut(key, norm.body, norm.contentType);
      } catch (e) {
        rewriteUnauth(e);
      }
    },

    async putIfMatch(
      key: string,
      body: BodyInit,
      contentType: string,
      expectedCrc32c: string,
    ): Promise<RecordPutIfMatchResult> {
      validateDataKey(key);
      if (typeof expectedCrc32c !== "string") {
        throw new BacklitError("invalid_argument", "expectedCrc32c must be a string");
      }
      const norm = normalizeBody(body, contentType);
      try {
        // A { matched: false } witness returns through the try untouched; only
        // a thrown unauthenticated is rewritten (a thrown forbidden propagates).
        return await getBackend().recordsPutIfMatch(key, norm.body, norm.contentType, expectedCrc32c);
      } catch (e) {
        rewriteUnauth(e);
      }
    },

    async delete(key: string): Promise<void> {
      validateDataKey(key);
      try {
        return await getBackend().recordsDelete(key);
      } catch (e) {
        rewriteUnauth(e);
      }
    },

    async list(prefix: string = "", opts?: ListOptions): Promise<ListPage<RecordListEntry>> {
      validateDataPrefix(prefix);
      try {
        return await getBackend().recordsList(prefix, opts);
      } catch (e) {
        rewriteUnauth(e);
      }
    },

    async getBatch(prefix: string = "", opts?: GetBatchOptions): Promise<ListPage<RecordBatchEntry>> {
      validateDataPrefix(prefix);
      try {
        return await getBackend().recordsGetBatch(prefix, opts);
      } catch (e) {
        rewriteUnauth(e);
      }
    },

    async getJSON<T = unknown>(key: string): Promise<RecordJSONResult<T>> {
      const r = await ns.get(key);
      return carryTags(r, { value: await decodeJSON<T>(r.value, key), crc32c: r.crc32c, owner: r.owner });
    },

    async getBatchJSON<T = unknown>(prefix: string = "", opts?: GetBatchOptions): Promise<ListPage<RecordBatchJSONEntry<T>>> {
      // The shared decoder preserves each entry's `owner` via its metadata
      // spread; the RecordBatchEntry → RecordBatchJSONEntry shape is the same
      // beyond the parsed value, so the cast is sound. ns.getBatch already
      // applied the unauthenticated rewrite.
      const page = await decodeBatchJSONPage<T, RecordBatchEntry>(await ns.getBatch(prefix, opts));
      return page as unknown as ListPage<RecordBatchJSONEntry<T>>;
    },

    async putJSON(key: string, value: unknown): Promise<PutResult> {
      return ns.put(key, encodeJSON(value), JSON_CONTENT_TYPE);
    },

    async putJSONIfMatch<T = unknown>(
      key: string,
      value: unknown,
      expectedCrc32c: string,
    ): Promise<RecordPutJSONIfMatchResult<T>> {
      const r = await ns.putIfMatch(key, encodeJSON(value), JSON_CONTENT_TYPE, expectedCrc32c);
      if (r.matched) return carryTags(r, { matched: true as const, crc32c: r.crc32c, owner: r.owner });
      return {
        matched: false,
        witness: carryTags(r.witness, {
          value: await decodeJSON<T>(r.witness.value, key),
          crc32c: r.witness.crc32c,
          owner: r.witness.owner,
        }),
      };
    },

    updateJSON<T = unknown>(
      key: string,
      mutate: (current: T | undefined) => T,
      options?: UpdateJSONOptions,
    ): Promise<RecordJSONResult<T>> {
      return runUpdateJSONWithOwner<T>((k) => ns.getJSON<T>(k), (k, v, exp) => ns.putJSONIfMatch<T>(k, v, exp), key, mutate, options);
    },

    async reassign(key: string, newOwnerUid: string): Promise<void> {
      validateDataKey(key);
      validateOwnerUid(newOwnerUid); // non-empty string check; throws before any fetch
      try {
        return await getBackend().recordsReassign(key, newOwnerUid);
      } catch (e) {
        rewriteUnauth(e);
      }
    },
  };

  return ns;
}
